import iconClose from '../../assets/iconCloseUP.svg'
import { AdPlayer, PlayerEvent } from '../adPlayer/AdPlayer'
import { Placement } from '../enums/OpenRTB'
import { PopoutPlacement } from '../enums/PopoutPlacement'
import EventManager from '../eventManager/EventManager'
import { Device } from '../helpers'
import { remove } from '../helpers/DOM'
import { doOnce } from '../helpers/doOnce'
import { getMutationObserver } from '../helpers/Observer'
import type WebModel from '../models/WebModel'
import { hasOutstream } from '../outstream/hasOutstream'
import { OUTSTREAM_REFRESH_TIME } from '../outstream/OutstreamRenderer'
import { mutationConfig, Slot } from './Slot'

const DEFAULT_MOBILE_TOP_MARGIN = 4
const BODY_CLASS = 'mediavine-video__has-sticky'
const MAX_MOBILE_WIDTH = 178
const MAX_DESKTOP_WIDTH = 300

/**
 * The UniversalPlayer slot is used to display Desktop Adhesion Outstream ads.
 * They appear in the same same spot as the Popout Video Player would.  They are
 * intended to be used when a page doesn't have a Mediavine video player on it.
 */
@hasOutstream
export class UniversalPlayer extends Slot {
  slotName = 'universal_player' // We're using the universal_player adunit in GAM for these ads.
  adUnitId = 'universalPlayer'
  refreshTime = OUTSTREAM_REFRESH_TIME
  popoutPlacement: PopoutPlacement

  // Don't want google to bid on UniversalPlayer ads.
  adXEligible = false
  // We don't want PSAs in this slot. We could accidentally end up with banner ads instead of the video ads they should be.
  psaEligible = false

  // Adhesion and UniversalPlayer slots need to override the default value for outstreamPlacement,
  // because they are not actually in the Article; they're Floating.
  outstreamPlacement = Placement.floating

  constructor(slotModel: ISlotModel, model: WebModel) {
    super(slotModel, model)
    this._sizes = [
      [1, 1],
      [300, 250]
    ]
    this.maxOutstreamWidth = Device.isMobileOrTablet
      ? MAX_MOBILE_WIDTH
      : MAX_DESKTOP_WIDTH
    this.outstreamPlacement = Placement.floating

    this.popoutPlacement = this.getPopoutPlacement()

    EventManager.on(EventManager.events.slotWrapperRenderEnded, (slot) => {
      if (slot === this) {
        // We set up the close button here to make sure the template is actually in the DOM
        this.setupCloseButton()
      }
    })
  }

  meetsOutstreamCriteria(): boolean {
    return (
      (!Device.isMobileOrTablet && this.model.universal_player_desktop) ||
      (Device.isMobileOrTablet && this.model.universal_player_mobile)
    )
  }

  // Adds listener to the close button created in the template.
  private setupCloseButton() {
    const closeButton = this.wrapper.querySelector(`.${this.adUnitId}_close`)

    if (closeButton) {
      closeButton.addEventListener('click', () => {
        this.onClose()
        return false
      })
    }
  }

  onClose() {
    this.cleanup()
    this.wrapper.style.display = 'none'
    document.body.classList.remove(BODY_CLASS)
    const el = this.wrapper
    if (el !== null && this.gSlot !== null) {
      window.googletag.destroySlots([this.gSlot])
      remove(el)
    }
  }

  // UniversalPlayer has a custom cleanup method because we
  // don't want to `display: none` the wrapper on cleanup.
  // (It allows the retry logic to continue attempting after an initial failure)
  cleanup(): void {
    //this.wrapper.style.display = 'none'
    getMutationObserver().disconnect(this.wrapper, mutationConfig)
  }

  onRefreshRetryFail() {
    this.onClose()
  }

  registerOutstreamEvents(player: AdPlayer) {
    player.onEvent(PlayerEvent.started, this.onPlayerStarted.bind(this))
  }

  // When the first Outstream Ad plays, we want to unhide the UniversalPlayer.
  // Then 2 seconds later, unhide the Universal Player's close button.
  onPlayerStarted = doOnce((evt) => {
    const twoSecs = 2000
    this.wrapper.style.visibility = 'visible'

    const closeButton = this.wrapper.querySelector(
      `.${this.adUnitId}_close`
    ) as HTMLElement
    setTimeout(() => {
      if (closeButton) {
        closeButton.style.display = 'initial'
      }
    }, twoSecs)
  })

  // Determines the placement of the sticky outstream player.
  private getPopoutPlacement(): PopoutPlacement {
    if (Device.isMobileOrTablet) {
      return (
        (this.model.videoSettings.mobile_popout_placement as PopoutPlacement) ||
        PopoutPlacement.bottomRight
      )
    }
    const stickyContentSide = !!this.model.videoSettings.sticky_content_side
    const contentSelector: any = this.model.content_selector || null
    const sidebarSelector: any = this.model.sidebar_btf_selector || null
    const contentEl = document.querySelector(contentSelector) as HTMLElement
    const sidebarEl = document.querySelector(sidebarSelector) as HTMLElement
    let contentLeft: number | boolean
    let sidebarLeft: number | boolean

    try {
      contentLeft = contentEl && contentEl.getBoundingClientRect().left
      sidebarLeft = sidebarEl && sidebarEl.getBoundingClientRect().left
    } catch (e) {
      sidebarLeft = false
      contentLeft = false
    }

    if (typeof sidebarLeft !== 'number' || typeof contentLeft !== 'number') {
      return PopoutPlacement.bottomRight
    }

    if (stickyContentSide) {
      return sidebarLeft < contentLeft
        ? PopoutPlacement.bottomRight
        : PopoutPlacement.bottomLeft
    }
    return sidebarLeft < contentLeft
      ? PopoutPlacement.bottomLeft
      : PopoutPlacement.bottomRight
  }

  // The universal player does not have normal (banner) adunits.
  // It only gets adunits from @hasOutstream.
  async adunits() {
    return []
  }

  sizeWrapper() {
    // Adding this class lets other things (Grow.me) know that we have a sticky video player on the page.
    document.body.classList.add(BODY_CLASS)

    const adHeight = Number(this.height)
    this.wrapper.style.maxWidth = `${this.maxOutstreamWidth}px`
    this.wrapper.style.height = `${adHeight}px`
    this.wrapper.style.minHeight = `${adHeight}px`

    // add top margin
    if (
      Device.isMobileOrTablet &&
      this.model.videoSettings.mobile_popout_placement.includes('top')
    ) {
      const videoSettings = this.model.videoSettings
      // If autoplay_mobile===true, we can be assured that they put thought into the
      // value of mobile_popout_top_margin
      const topValue = videoSettings.autoplay_mobile
        ? videoSettings.mobile_popout_top_margin
        : DEFAULT_MOBILE_TOP_MARGIN
      this.wrapper.style.top = `${topValue}px`
    }

    // this.wrapper.style.display = 'block'
  }

  template(): string {
    const placement = this.popoutPlacement.split('_')
    // "top" or "bottom"
    const verticalPlacement = `${this.adUnitId}__${placement[0]}`
    // "left", "right", or "center"
    const horizontalPlacement = `${this.adUnitId}__${placement[1]}`

    return `        
      <div id='${this.wrapperId}' class='adunitwrapper ${horizontalPlacement} ${verticalPlacement} ${this.adUnitId}_wrapper mv-empty-wrapper ' data-wrapper='${this.id}' data-nosnippet style="visibility: hidden;">
        <span class='${this.adUnitId}_close mv_unbutton' role='button' aria-label='Close ad' style='display: none;' >
          <img id="iconCloseSvg" data-pin-nopin=true src=${iconClose} alt='' aria-label='Close Ad' />
        </span>
        
        <div id='${this.id}' class='adunit'> </div> 
      </div>
    `
  }
}
